import { useEffect, useMemo, useState } from "react";
import { formatMoney } from "../../../features/utils/formatMoney";
import { clone } from "../../../features/utils/objClone";
import {
  getIntraverseMarkupFromBreakdown,
  isAutoGeneratedBreakdownLine,
  resolveOriginalAppliedAdjustment,
  reverseOriginalAdjustment,
} from "../../../utils/booking/refundQuote";
import PassengerBreakdownCard, {
  BASE_BREAKDOWN,
  applyRefundReturnAdjustment,
  buildDeductibleBreakdownRow,
  defaultMarkupTargets,
  fieldKey,
  inferMarkupFromBreakdown,
  isComputedDeductibleLine,
  isReturnLine,
} from "./PassengerBreakdownCard";

/** Reverse agent return → airline return (refund markup takes off the return). */
function reverseRefundReturnAmount(selling, markup) {
  const sell = Math.max(0, Number(selling) || 0);
  const raw = Number(markup?.amount ?? markup?.markupValue);
  if (!Number.isFinite(raw) || raw <= 0) return sell;

  const variant = markup?.variant || markup?.markupVariant || "Markup";
  const type = markup?.type || markup?.markupType || "Fixed";

  if (type === "Percentage") {
    const pct = raw / 100;
    // Markup: agent = airline * (1 - pct) → airline = agent / (1 - pct)
    if (variant === "Markup" && pct < 1) return sell / (1 - pct);
    // Markdown: agent = airline * (1 + pct)
    if (variant === "Markdown") return sell / (1 + pct);
    return sell;
  }

  // Markup: agent = airline - raw → airline = agent + raw
  if (variant === "Markup") return sell + raw;
  // Markdown: agent = airline + raw
  return Math.max(0, sell - raw);
}

function pickMarkup(row = {}, order = {}) {
  const variant =
    row?.markupVariant || order?.markupVariant || order?.markup_variant;
  const type = row?.markupType || order?.markupType || order?.markup_type;
  const amount =
    row?.markupValue ?? order?.markupValue ?? order?.markup_value ?? "";

  if (variant || type || amount !== "") {
    return {
      variant: variant || "Markup",
      type: type || "Fixed",
      amount: amount === "" || amount == null ? "" : amount,
    };
  }
  return null;
}

function pickMarkupTargets(row = {}) {
  if (row?.markupTargets) {
    const targets = { ...defaultMarkupTargets, ...row.markupTargets };
    // Drop legacy fee-line markup keys if present
    delete targets.Penalty;
    delete targets["Supplier Fee"];
    delete targets["Refund Fee"];
    delete targets["Non-refundable Fees (All Taxes)"];
    return targets;
  }
  if (
    row?.markupApplyBaseReturn != null ||
    row?.markupApplyTaxReturn != null ||
    row?.markupApplyRefundCharge != null ||
    row?.markupApplyRefundFee != null
  ) {
    return {
      "Base Return":
        row.markupApplyBaseReturn ??
        row.markupApplyRefundFee ??
        row.markupApplyRefundCharge ??
        defaultMarkupTargets["Base Return"],
      "Tax Return":
        row.markupApplyTaxReturn ?? defaultMarkupTargets["Tax Return"],
    };
  }
  return null;
}

function parseMoneyField(price) {
  if (typeof price !== "string") return Number(price || 0);
  const numeric = parseFloat(
    price.replace(/^[A-Za-z\s]+/, "").replaceAll(",", ""),
  );
  return Number.isFinite(numeric) ? numeric : 0;
}

/**
 * Ensure each breakdown row has airlineAmount (airline/original) while keeping
 * amount as the agent figure after quote Fixed/% markup when already set.
 */
export function toOriginalTicketPassengerPrice(list) {
  return (list || []).map((item) => {
    const breakDown = (item?.breakDown || []).map((row) => {
      // Booking Markup is signed (markdown is negative) — do not clamp to 0.
      if (isAutoGeneratedBreakdownLine(row) && !isComputedDeductibleLine(row)) {
        const signed = Number(
          row?.airlineAmount != null && row?.airlineAmount !== ""
            ? row.airlineAmount
            : row?.amount || 0,
        );
        const rounded = Math.round((Number(signed) || 0) * 100) / 100;
        return {
          ...row,
          amount: rounded,
          airlineAmount: rounded,
          isDeducted: false,
        };
      }
      const airline = Math.max(
        0,
        Number(
          row?.airlineAmount != null && row?.airlineAmount !== ""
            ? row.airlineAmount
            : row?.amount || 0,
        ),
      );
      const agent = Math.max(
        0,
        Number(
          row?.amount != null && row?.amount !== ""
            ? row.amount
            : airline,
        ),
      );
      return {
        ...row,
        amount: agent,
        airlineAmount: airline,
      };
    });
    return { ...item, breakDown };
  });
}

export function getBookingPricingRoot(defData) {
  // ManageOrder wraps flightBooking as [booking]; OrderList may pass the object.
  // Prefer a plain booking object with adult/child/infant.
  const candidates = [
    defData?.flightObj,
    defData?.flightBooking,
    Array.isArray(defData?.flightBooking)
      ? defData.flightBooking[0]
      : null,
  ];
  for (const raw of candidates) {
    if (!raw) continue;
    const booking = Array.isArray(raw) ? raw[0] : raw;
    if (booking && (booking.adult || booking.child || booking.infant)) {
      return booking;
    }
    if (booking && typeof booking === "object" && !Array.isArray(booking)) {
      return booking;
    }
  }
  return {};
}

/** Unit ticket / base / tax for a pax type from flightBooking.adult|child|infant */
export function getPaxTypeUnitPricing(defData, travelerType, passengerCount) {
  const travellerMap = {
    ADT: "ADT",
    Adult: "ADT",
    CHD: "CHD",
    CNN: "CHD",
    Child: "CHD",
    INS: "INF",
    INF: "INF",
    Infant: "INF",
  };
  const normalized = travellerMap[travelerType] || travelerType || "ADT";
  const booking = getBookingPricingRoot(defData);
  const travelerDict = {
    ADT: booking?.adult || {},
    CHD: booking?.child || {},
    INF: booking?.infant || {},
  };
  const pricing = travelerDict[normalized] || {};
  const count = Math.max(
    Number(pricing?.passengerCount) || Number(passengerCount) || 1,
    1,
  );
  return {
    normalized,
    count,
    unitPrice: parseMoneyField(pricing?.totalPrice) / count,
    unitBase: parseMoneyField(pricing?.basePrice) / count,
    unitTax: parseMoneyField(pricing?.taxes) / count,
  };
}

function normalizeSavedBreakdown(breakDown = [], markup, markupTargets) {
  // Drop auto Deductible / Booking Markup — UI only edits the four ops lines
  const rows = (Array.isArray(breakDown) ? breakDown : []).filter(
    (row) => !isAutoGeneratedBreakdownLine(row),
  );

  return BASE_BREAKDOWN.map((base) => {
    const sameName = rows.filter((row) => fieldKey(row?.name) === base.name);
    const saved =
      sameName.find((row) => row?.airlineAmount != null) ||
      sameName.find((row) => row?.isDeducted === base.isDeducted) ||
      sameName[0];

    if (saved?.airlineAmount != null) {
      return {
        ...base,
        amount: Math.max(0, Number(saved.airlineAmount) || 0),
      };
    }

    if (saved) {
      const selling = Number(saved?.amount || 0);
      const applies = Boolean(
        (markupTargets || defaultMarkupTargets)[base.name],
      );
    const airline = applies
      ? Math.round(reverseRefundReturnAmount(selling, markup) * 100) / 100
      : selling;
      return {
        ...base,
        amount: Math.max(0, airline),
      };
    }

    return { ...base };
  });
}

export const PriceBreakdown = ({ data: defData, callback }) => {
  const [passengerPrice, setPassengerPrice] = useState([]);
  const [lock, setLock] = useState(true);

  const travellerMap = useMemo(
    () => ({
      ADT: "ADT",
      Adult: "ADT",
      CHD: "CHD",
      CNN: "CHD",
      Child: "CHD",
      INS: "INF",
      INF: "INF",
      Infant: "INF",
    }),
    [],
  );

  const travellerLabel = {
    ADT: "Adult",
    CHD: "Child",
    INF: "Infant",
  };

  const passengerIds = useMemo(() => defData?.passengerIds || [], [defData]);

  const selectedTravellers = useMemo(() => {
    const booking = getBookingPricingRoot(defData);
    const all = booking?.travelers || [];
    return all.filter((t) => passengerIds.includes(t?.id));
  }, [defData, passengerIds]);

  const travellersByTypeCount = useMemo(() => {
    const map = {};
    for (const t of selectedTravellers) {
      const normalized =
        travellerMap[t?.travelerType] || t?.travelerType || "ADT";
      map[normalized] = (map[normalized] || 0) + 1;
    }
    return map;
  }, [selectedTravellers, travellerMap]);

  useEffect(() => {
    let cached = null;
    try {
      cached = JSON.parse(
        sessionStorage.getItem(`refundQuoteAdmin:${defData?._id}`) || "null",
      );
    } catch (_) {
      cached = null;
    }

    const orderMarkup = pickMarkup({}, defData || {});
    const source =
      defData?.passengerPrice?.length
        ? defData.passengerPrice
        : cached?.passengerPrice?.length
          ? cached.passengerPrice
          : null;

    if (source?.length) {
      // Backend often omits markup fields — infer from Base/Tax airline vs amount
      // (single passenger-type for now).
      const next = source.map((p) => {
        const inferred = inferMarkupFromBreakdown(p?.breakDown);
        const savedMarkup = pickMarkup(p, defData) || orderMarkup;
        const hasSavedMarkup =
          savedMarkup &&
          savedMarkup.amount !== "" &&
          savedMarkup.amount != null &&
          Number(savedMarkup.amount) > 0;
        const markup = hasSavedMarkup ? savedMarkup : inferred.markup;
        const markupTargets =
          pickMarkupTargets(p) ||
          inferred.markupTargets ||
          { ...defaultMarkupTargets };
        return {
          travelerType: p?.travelerType,
          markup,
          markupTargets,
          // Backend Intraverse Markup (after quote) — keep for estimate add-back
          intraverseMarkup: getIntraverseMarkupFromBreakdown(p?.breakDown),
          breakDown: normalizeSavedBreakdown(
            p?.breakDown,
            markup,
            markupTargets,
          ),
        };
      });
      const unique = next.filter(
        (item, i, arr) =>
          arr.findIndex((x) => x?.travelerType === item?.travelerType) === i,
      );
      setPassengerPrice(unique);
      return;
    }

    const init = selectedTravellers
      .map((t) => ({
        travelerType: t?.travelerType,
        markup: { variant: "Markup", type: "Fixed", amount: "" },
        markupTargets: { ...defaultMarkupTargets },
        breakDown: clone(BASE_BREAKDOWN),
      }))
      .filter(
        (item, i, arr) =>
          arr.findIndex((x) => x?.travelerType === item?.travelerType) === i,
      );
    setPassengerPrice(init);
    // eslint-disable-next-line
  }, [defData]);

  useEffect(() => {
    if (lock) setLock(false);
    else {
      callback &&
        callback({
          passengerPrice: buildPayloadPassengerPrice(passengerPrice),
          fee: defData?.fee || 0,
          refundAmount: defData?.payable || 0,
        });
    }
    // eslint-disable-next-line
  }, [passengerPrice]);

  function buildPayloadPassengerPrice(list) {
    return (list || []).map((item) => {
      const markup = item?.markup || {
        variant: "Markup",
        type: "Fixed",
        amount: "",
      };
      const markupTargets = {
        ...defaultMarkupTargets,
        ...(item?.markupTargets || {}),
      };

      const { passengerCount } = getIntraverseFeeMeta(item?.travelerType);
      const { unitPrice, unitBase } = getPaxTypeUnitPricing(
        defData,
        item?.travelerType,
        passengerCount,
      );
      const { underlyingValue } = reverseOriginalAdjustment(
        unitPrice,
        bookingAdjustment,
        unitBase,
      );

      // Ops lines only. Deductible for FE/backend audit.
      // Do not send Booking Markup / Intraverse Markup — backend ignores and
      // returns Intraverse Markup after quote.
      const breakDown = (item?.breakDown || BASE_BREAKDOWN)
        .filter((row) => !isAutoGeneratedBreakdownLine(row))
        .map((row) => {
          const key = fieldKey(row?.name);
          const base =
            BASE_BREAKDOWN.find((b) => b.name === key) || {
              isDeducted: row?.isDeducted !== false,
              isSupplierFee: Boolean(row?.isSupplierFee),
            };
          const airline = Math.max(0, Number(row?.amount) || 0);
          const applies =
            Boolean(markupTargets[key]) &&
            isReturnLine({ isDeducted: base.isDeducted });
          const selling = applies
            ? applyRefundReturnAdjustment(airline, markup)
            : airline;
          const payload = {
            name: key,
            amount: selling,
            airlineAmount: airline,
            isDeducted: Boolean(base.isDeducted),
          };
          if (base.isSupplierFee) payload.isSupplierFee = true;
          return payload;
        });

      const baseRow = breakDown.find((r) => r.name === "Base Return");
      const taxRow = breakDown.find((r) => r.name === "Tax Return");
      breakDown.push(
        buildDeductibleBreakdownRow({
          unitPrice,
          deductibleBasis: underlyingValue,
          baseAirline: baseRow?.airlineAmount,
          taxAirline: taxRow?.airlineAmount,
          baseAgent: baseRow?.amount,
          taxAgent: taxRow?.amount,
        }),
      );

      return {
        travelerType: item?.travelerType,
        breakDown,
        markupVariant: markup.variant || "Markup",
        markupType: markup.type || "Fixed",
        markupValue: markup.amount === "" ? 0 : Number(markup.amount) || 0,
        markupTargets,
        markupApplyBaseReturn: Boolean(markupTargets["Base Return"]),
        markupApplyTaxReturn: Boolean(markupTargets["Tax Return"]),
      };
    });
  }

  const getIntraverseFeeMeta = (travelerType) => {
    const normalized = travellerMap[travelerType] || travelerType || "ADT";

    const intraverseTravelerFee = defData?.intraverseFeeBreakdown?.find(
      (fee) => travellerMap[fee?.passengerType] === normalized,
    );

    const fallbackServiceFee =
      Number(defData?.intraverseFee || 0) / Math.max(passengerIds.length, 1);

    const passengerCount = Math.max(
      intraverseTravelerFee?.passengerCount ||
        travellersByTypeCount[normalized] ||
        1,
      1,
    );

    const serviceFee =
      intraverseTravelerFee?.amount != null
        ? Number(intraverseTravelerFee.amount) || 0
        : fallbackServiceFee;

    return {
      normalized,
      serviceFee,
      passengerCount,
    };
  };

  const handleSaveBreakdown = (
    travelerType,
    nextBreakDown,
    nextMarkup,
    nextTargets,
  ) => {
    setPassengerPrice((prev) => {
      const list = clone(prev) || [];
      const exists = list.some((p) => p?.travelerType === travelerType);
      if (!exists) {
        list.push({
          travelerType,
          breakDown: clone(nextBreakDown || BASE_BREAKDOWN),
          markup: nextMarkup,
          markupTargets: nextTargets,
        });
        return list;
      }
      return list.map((p) =>
        p?.travelerType === travelerType
          ? {
              ...p,
              breakDown: clone(nextBreakDown || []),
              markup: nextMarkup || p.markup,
              markupTargets: nextTargets || p.markupTargets,
              // Keep backend Intraverse Markup across edits
              intraverseMarkup: p.intraverseMarkup,
            }
          : p,
      );
    });
  };

  const uniquePassengerPrice = useMemo(() => {
    const arr = passengerPrice || [];
    return arr.filter(
      (item, i) =>
        arr.findIndex((x) => x?.travelerType === item?.travelerType) === i,
    );
  }, [passengerPrice]);

  const bookingAdjustment = useMemo(
    () => resolveOriginalAppliedAdjustment(defData),
    [defData],
  );

  return (
    <div className="w-full flex flex-col gap-2 mt-2">
      {defData?.supplierRefundAmount ? (
        <div className="rounded-lg border border-[#E8EEF5] bg-white px-3 py-2 space-y-1.5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-medium text-[#6D7C94]">
                Supplier refund
              </div>
              <div className="text-[10px] text-[#94A3B8]">
                Airline / supplier amount (auto)
              </div>
            </div>
            <div className="text-sm font-semibold text-[#0F172A]">
              {formatMoney(defData?.supplierRefundAmount)}
            </div>
          </div>
          {Number(defData?.intraverseRefundMarkup || 0) !== 0 ? (
            <div className="flex items-center justify-between border-t border-[#F1F5F9] pt-1.5">
              <div>
                <div className="text-xs font-medium text-[#6D7C94]">
                  Intraverse refund markup
                </div>
                <div className="text-[10px] text-[#94A3B8]">
                  supplier − payable (platform keep)
                </div>
              </div>
              <div className="text-sm font-semibold text-emerald-700">
                {formatMoney(defData?.intraverseRefundMarkup)}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="rounded-lg border border-[#E8EEF5] bg-[#F8FAFC] px-3 py-2 text-[11px] text-[#64748B]">
        Enter airline Base/Tax, penalty and supplier fee. Markup add-back is an{" "}
        <span className="font-medium text-[#0F172A]">estimate</span> until the
        quote returns <span className="font-medium text-[#0F172A]">Intraverse Markup</span>
        {" "}from the backend — then the estimate uses that amount.
      </div>

      <div className="space-y-2">
        {uniquePassengerPrice.map((item, index) => {
          const { normalized, serviceFee, passengerCount } =
            getIntraverseFeeMeta(item?.travelerType);
          const { unitPrice, unitBase, unitTax } = getPaxTypeUnitPricing(
            defData,
            item?.travelerType,
            passengerCount,
          );

          return (
            <PassengerBreakdownCard
              key={index}
              normalized={normalized}
              serviceFee={serviceFee}
              passengerCount={passengerCount}
              item={item}
              travelerType={item?.travelerType}
              travellerLabel={travellerLabel}
              handleSaveBreakdown={handleSaveBreakdown}
              unitPrice={unitPrice}
              unitBase={unitBase}
              unitTax={unitTax}
              bookingAdjustment={bookingAdjustment}
              intraverseMarkup={item?.intraverseMarkup}
            />
          );
        })}
      </div>
    </div>
  );
};
